import * as React from "react";
import { PanelLeftOpen, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import graftusLogo from "@/assets/graftus-logo.svg";
import graftusIconMark from "@/assets/graftus-g-logo.png";
import { Link, useLocation } from "react-router-dom";
import { useSignOut } from "react-firebase-hooks/auth";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { ref } from "firebase/storage";
import { storage } from "@/config/firebaseConfig";

import { NavChats } from "@/components/nav-chats";
import { NavMain, type NavMainItem } from "@/components/nav-main";
import { NotificationBell } from "@/components/NotificationBell";
import { NavUser } from "@/components/nav-user";
import { OrgScopeSwitcher } from "@/components/sidebar/OrgScopeSwitcher";
import { ProductSwitcher } from "@/components/sidebar/ProductSwitcher";
import { SidebarAskRow } from "@/components/sidebar/SidebarAskRow";
import { SidebarCreateAdsCta } from "@/components/sidebar-create-ads-cta";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { auth } from "@/config/firebaseConfig";
import { useProfile } from "@/context/ProfileContext";
import { useProductScope } from "@/context/ProductScopeContext";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import { useVisibleSidebarNavFeatures } from "@/hooks/use-sidebar-nav-features";
import {
  getActiveSidebarFeatureId,
  isFeatureSubtreeActive,
  SIDEBAR_NAV_GROUPS,
  type SidebarNavFeature,
} from "@/config/sidebar-nav";

/**
 * Exactly one row lights: the feature that owns the longest prefix of the
 * current path (`getActiveSidebarFeatureId`), so Ask Coach (`/coach`) stays
 * dark on `/coach/teams` and Teams stays lit on `/coach/members/:id`.
 */
function featuresToNavItems(
  features: SidebarNavFeature[],
  activeId: string | undefined,
  t: TFunction<"nav">,
): NavMainItem[] {
  return features.map((feature) => ({
    title: t(feature.titleKey),
    url: feature.url ?? feature.children?.[0]?.url ?? "#",
    icon: feature.icon,
    titleBadge: feature.titleBadge,
    isActive: feature.id === activeId,
  }));
}

export default function AppSidebar({
  className,
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { t: tNav } = useTranslation("nav");
  const { memberProfile, businessProfile } = useProfile();
  const { state, isMobile, setOpen } = useSidebar();
  const [signOut] = useSignOut(auth);

  // When collapsed, clicking any empty region of the rail expands the sidebar.
  // Clicks that land on interactive elements (nav icon buttons, avatar menu,
  // team switcher, etc.) keep their own behavior and are not swallowed.
  const handleCollapsedRailClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (state !== "collapsed" || isMobile) return;
      const target = event.target as HTMLElement;
      if (
        target.closest(
          'a, button, input, select, textarea, [role="button"], [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [role="switch"], [role="tab"]',
        )
      ) {
        return;
      }
      setOpen(true);
    },
    [state, isMobile, setOpen],
  );

  const [avatarUrl] = useDownloadURL(
    memberProfile?.uid
      ? ref(
          storage,
          `businessMembersAvatars/${memberProfile.uid}/profile_image`,
        )
      : undefined,
  );

  const [businessLogoUrl] = useDownloadURL(
    businessProfile?.uid
      ? ref(
          storage,
          `businessUsersAvatars/${businessProfile.uid}/profile_image`,
        )
      : undefined,
  );

  const showIntelligence = useRemoteConfigBoolean("show_intelligence");
  const visibleFeatures = useVisibleSidebarNavFeatures();
  const { activeProduct } = useProductScope();

  // Flat groups: Dashboard first (ungrouped), then the *active* product's own
  // group only (GRA-78) — Coach, Recruit and Leads are separate workspaces,
  // never shown mixed together. Settings is kept in the config for title
  // lookups but rendered behind the footer gear.
  const mainFeatures = visibleFeatures.filter((f) => !f.hiddenFromNav);
  const activeId = getActiveSidebarFeatureId(mainFeatures, location.pathname);
  const toNavItems = (features: SidebarNavFeature[]) =>
    featuresToNavItems(features, activeId, tNav);
  // Overview is ungrouped in the config (it has no unlabeled section of its
  // own to sit in otherwise), but it only works for Coach - Recruit and Leads
  // get their own landing pages - so it's hidden rather than shown broken
  // outside the Coach workspace (GRA-78).
  const todayItems = toNavItems(
    mainFeatures.filter(
      (f) => !f.group && (f.id !== "overview" || activeProduct === "coach"),
    ),
  );
  const productGroups = SIDEBAR_NAV_GROUPS.filter(
    (group) => group === activeProduct,
  )
    .map((group) => ({
      group,
      items: toNavItems(mainFeatures.filter((f) => f.group === group)),
    }))
    .filter(({ items }) => items.length > 0);

  // The footer Settings row is stable across every product (GRA-78) and
  // stays behind every Settings child in the nav config (including /apps,
  // which lives outside the /settings prefix). It was briefly relabeled
  // "Company" to disambiguate it from per-product settings rows, but Coach's
  // got folded back into this same hub and Recruit never had one, so the
  // only one left is Leads' own "Leads-inställningar" - not enough of a
  // naming conflict to justify the label change anymore.
  const settingsFeature = visibleFeatures.find((f) => f.id === "settings");
  const settingsActive = settingsFeature
    ? isFeatureSubtreeActive(settingsFeature, location.pathname)
    : false;

  if (!memberProfile) {
    return (
      <Sidebar collapsible="icon" className={className} {...props}>
        <SidebarHeader className="p-0 gap-0">
          <div className="flex items-center px-4 h-12 shrink-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
            <img
              src={graftusLogo}
              className="h-6 group-data-[collapsible=icon]:hidden"
              alt="Graftus"
            />
            <SidebarTrigger
              className="ml-auto group-data-[collapsible=icon]:ml-0"
              aria-label={tNav("sidebar.toggle")}
            />
          </div>
          <div className="p-2">
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        </SidebarHeader>
        <SidebarContent>
          <div className="p-4 space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-full rounded-md" />
            <Skeleton className="h-8 w-full rounded-md" />
            <Skeleton className="h-4 w-20 mt-4" />
            <Skeleton className="h-8 w-full rounded-md" />
            <Skeleton className="h-8 w-full rounded-md" />
          </div>
        </SidebarContent>
        <SidebarFooter>
          <div className="flex items-center gap-2 p-2">
            <Skeleton className="size-8 rounded-lg" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        </SidebarFooter>
      </Sidebar>
    );
  }

  const userForNav = {
    name: memberProfile.name ?? "",
    email: memberProfile.email ?? "",
    avatar: avatarUrl,
  };

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        className,
        // Signal that clicking the collapsed rail re-expands the sidebar,
        // matching the SidebarRail's own e-resize (right arrow) cursor. Real
        // interactive controls keep a pointer cursor so they still read as
        // directly clickable rather than as expand affordances.
        "group-data-[collapsible=icon]:cursor-e-resize",
        "group-data-[collapsible=icon]:[&_a]:cursor-pointer group-data-[collapsible=icon]:[&_button]:cursor-pointer group-data-[collapsible=icon]:[&_[role=button]]:cursor-pointer",
      )}
      onClick={handleCollapsedRailClick}
      {...props}
    >
      <SidebarHeader className="p-0 gap-0">
        <div className="flex items-center px-4 h-12 shrink-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <img
            src={graftusLogo}
            className="h-6 group-data-[collapsible=icon]:hidden"
            alt="Graftus"
          />
          {/*
            Collapsed rail: the "G" mark crossfades to an "Open sidebar" button
            while the cursor is anywhere inside the sidebar (group-hover). The
            button is also keyboard-focusable and reveals on focus-visible.
          */}
          <div className="relative hidden h-6 w-6 group-data-[collapsible=icon]:block">
            <img
              src={graftusIconMark}
              className="absolute inset-0 h-6 w-6 object-contain transition-opacity duration-200 group-hover:opacity-0 dark:invert"
              alt="Graftus"
              aria-hidden="true"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={tNav("sidebar.open")}
              onClick={() => setOpen(true)}
              className="absolute inset-0 h-6 w-6 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100"
            >
              <PanelLeftOpen />
            </Button>
          </div>
          <div className="ml-auto flex items-center group-data-[collapsible=icon]:hidden">
            <NotificationBell />
            <SidebarTrigger aria-label={tNav("sidebar.toggle")} />
          </div>
        </div>
        <div className="p-2">
          <OrgScopeSwitcher
            orgName={businessProfile?.business ?? "Graftus"}
            orgLogoUrl={businessLogoUrl}
            isLoading={!businessProfile}
          />
          <div className="mt-2">
            <ProductSwitcher />
          </div>
          {showIntelligence ? (
            <div className="mt-2 group-data-[collapsible=icon]:hidden">
              <SidebarAskRow />
            </div>
          ) : null}
        </div>
        <SidebarCreateAdsCta />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={todayItems} pathname={location.pathname} />
        {productGroups.map(({ group, items }) => (
          <NavMain
            key={group}
            items={items}
            pathname={location.pathname}
            groupLabel={tNav(`groups.${group}`)}
          />
        ))}
        <NavChats />
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={tNav("settings")}
              isActive={settingsActive}
            >
              <Link
                to="/settings"
                aria-current={settingsActive ? "page" : undefined}
              >
                <Settings aria-hidden="true" />
                <span>{tNav("settings")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser user={userForNav} onSignOut={() => void signOut()} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
